import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import ChartBase from './ChartBase';
import { resolveChartData, getItemValue, getItemLabel, getItemId, getMaxValue, getMinValue, sortDataByValue } from './ChartData';
import '../../styles/chart-styles.css';

const GradientChart = ({ 
  data, 
  moduleId, 
  dataIndex, 
  title, 
  subtitle,
  showLabels = true, 
  animate = true,
  className = '',
  linkTo = null,
  linkKey = 'id',
  actionLink = null,
  actionLinkText = 'Explore',
  ...props 
}) => {
  const { getModuleData, theme, themes } = useTheme();
  
  // Get the entire module data array
  const moduleData = getModuleData(moduleId);
  const chartArray = resolveChartData(data, moduleData);
  
  // Sort data in decreasing order
  const sortedData = sortDataByValue(chartArray, 'desc');
  
  const maxValue = getMaxValue(chartArray);
  const minValue = getMinValue(chartArray);
  
  const currentTheme = themes[theme];
  const bgColor = currentTheme.bgPrimary;
  const primaryColor = currentTheme.textPrimary;
  const darkTextColor = currentTheme.textPrimary;
  const lightTextColor = currentTheme.accentContrast;
  
  // Function to interpolate between two colors
  const interpolateColor = (color1, color2, factor) => {
    const hex = (c) => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    
    const r1 = parseInt(color1.slice(1, 3), 16);
    const g1 = parseInt(color1.slice(3, 5), 16);
    const b1 = parseInt(color1.slice(5, 7), 16);
    
    const r2 = parseInt(color2.slice(1, 3), 16);
    const g2 = parseInt(color2.slice(3, 5), 16);
    const b2 = parseInt(color2.slice(5, 7), 16);
    
    const r = Math.round(r1 + (r2 - r1) * factor);
    const g = Math.round(g1 + (g2 - g1) * factor);
    const b = Math.round(b1 + (b2 - b1) * factor);
    
    return `#${hex(r)}${hex(g)}${hex(b)}`;
  };
  
  // Function to calculate luminance of a color
  const getLuminance = (hexColor) => {
    const r = parseInt(hexColor.slice(1, 3), 16) / 255;
    const g = parseInt(hexColor.slice(3, 5), 16) / 255;
    const b = parseInt(hexColor.slice(5, 7), 16) / 255;
    
    const linearR = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
    const linearG = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
    const linearB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
    
    return 0.2126 * linearR + 0.7152 * linearG + 0.0722 * linearB;
  };
  
  // Function to calculate contrast ratio between two colors
  const getContrastRatio = (color1, color2) => {
    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);
    return (lighter + 0.05) / (darker + 0.05);
  };
  
  // Function to get contrasting text color from theme
  const getContrastColor = (hexColor) => {
    const contrastWithDark = getContrastRatio(hexColor, darkTextColor);
    const contrastWithLight = getContrastRatio(hexColor, lightTextColor);
    return contrastWithDark > contrastWithLight ? darkTextColor : lightTextColor;
  };
  
  // Get color based on value percentage
  const getColor = (value) => {
    const percentage = (value - minValue) / (maxValue - minValue || 1);
    return interpolateColor(bgColor, primaryColor, percentage);
  };

  return (
    <ChartBase title={title} subtitle={subtitle} actionLink={actionLink} actionLinkText={actionLinkText}>
      <div className="flex flex-col h-full">
        {/* Gradient boxes container */}
        <div className="flex flex-col justify-center space-y-2">
          {sortedData.map((item, index) => {
            const value = getItemValue(item);
            const label = getItemLabel(item, index);
            const id = getItemId(item, linkKey);
            const color = getColor(value);
            const textColor = getContrastColor(color);
            
            const labelContent = linkTo && id ? (
              <Link 
                to={`${linkTo}/${id}`}
                className="text-xs font-medium hover:underline"
                style={{ color: textColor }}
              >
                {label || `Item ${index + 1}`}
              </Link>
            ) : (
              <span className="text-xs font-medium" style={{ color: textColor }}>
                {label || `Item ${index + 1}`}
              </span>
            );
            
            return (
              <div 
                key={index} 
                className="flex items-center space-x-3"
                style={{ minHeight: '24px' }}
              >
                <div 
                  className="rounded transition-all duration-300 hover:opacity-80"
                  style={{ 
                    backgroundColor: color,
                    width: '100%',
                    minHeight: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 12px'
                  }}
                >
                  {labelContent}
                  <span className="text-xs font-medium" style={{ color: textColor }}>
                    {value}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ChartBase>
  );
};

export default GradientChart;
